import { UseMutationOptions, useMutation, useQueryClient } from 'react-query';
import { RobotDevice } from '../../types';
import { setRobotVolume } from '../api';
import { robotAudioKeys } from './keys';

export const useSetRobotVolume = (
  robot: RobotDevice | undefined,
  options?: UseMutationOptions<
    void,
    unknown,
    number,
    { previousValue: unknown }
  >
) => {
  const queryClient = useQueryClient();
  const robotId = robot?.id ?? '';

  const mutation = useMutation({
    mutationFn: async (newVolume) => {
      await setRobotVolume(robot?.websocketConnection?.ipAddress, newVolume);
    },

    onMutate: async (newValue) => {
      const queryKey = robotAudioKeys.audio(robotId);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(queryKey);

      // Snapshot the previous value
      const previousValue = queryClient.getQueryData(queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, newValue);

      // Return a context with the previous value
      return { previousValue };
    },

    onSettled: (_data, error, _variables, context) => {
      if (error) {
        queryClient.setQueryData(
          robotAudioKeys.audio(robotId),
          context?.previousValue
        );
      }

      // Invalidate all queries that may contain stale data due to this setting update
      queryClient.invalidateQueries(robotAudioKeys.audio(robotId));
    },
    ...options,
  });

  return mutation;
};
